import { ChevronDown, ChevronLeft, ChevronRight, Eye, EyeOff, Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { EntryCircleIcon, StopCrossIcon, TakeProfitDiamondIcon } from './WorkbenchDrawIcons'
import type { IdeaRole } from './tradeIdeaVocab'
import type { TradeIdea, TradeIdeaMark } from './tradeIdeas'

/**
 * Every trade idea on this chart, as a tree. Idea rows open to their levels,
 * each with an eye and a trash — a level can be taken off the canvas without
 * being taken out of the plan (a hidden level still reaches the AI).
 *
 * ROW CONTROLS ARE 24px AND ADJACENT, not 17px with gaps between them.
 *
 * Reported as "I can't select hide/show or delete — it vanishes". Measured in
 * the running app, the icons were 17px boxes separated by 6px of dead row, at
 * the far right of a 28px row that gave no hover feedback at all. Every miss
 * landed on the title instead — a 167px target whose job is to open the level,
 * which closes this panel and moves the chart. So a near-miss on a bin did not
 * merely do nothing: it took the panel away, which is what "it vanishes"
 * describes.
 *
 * Hence: one control cluster with no dead gaps inside it, `h-6 w-6` targets a
 * 28px row can still hold, and a hover tint on the row so the trader can see
 * which one their pointer owns before committing to the 17px of icon in it.
 */

const ROLE_ICON: Record<IdeaRole, typeof EntryCircleIcon> = {
  entry: EntryCircleIcon,
  sl: StopCrossIcon,
  tp: TakeProfitDiamondIcon,
}

/**
 * How much of the pane's left edge this panel owns.
 *
 * Exported because the floating strip has to clear it, and a second copy of
 * "256" over there is how the strip came to be drawn across these rows.
 */
export const IDEA_PANEL_W = 256

export interface WorkbenchTradeIdeaPanelProps {
  open: boolean
  ideas: TradeIdea[]
  activeIdeaId: string | null
  expanded: ReadonlySet<string>
  onToggleExpand: (ideaId: string) => void
  onSelectIdea: (ideaId: string) => void
  onSelectMark: (markId: string) => void
  onToggleHideMark: (markId: string, hidden: boolean) => void
  onDeleteMark: (markId: string) => void
  onDeleteIdea: (ideaId: string) => void
  onClose: () => void
}

function MarkRow({
  mark,
  onSelect,
  onToggleHide,
  onDelete,
}: {
  mark: TradeIdeaMark
  onSelect: () => void
  onToggleHide: () => void
  onDelete: () => void
}) {
  const Icon = ROLE_ICON[mark.role]
  return (
    <div
      className={cn(
        'group/mark flex h-7 items-center gap-1 pl-9 pr-1',
        mark.hidden ? 'text-muted-foreground/60' : 'text-foreground/70',
        // The row, not just its title, is what the pointer owns.
        'hover:bg-muted',
      )}
    >
      <button
        type="button"
        onClick={onSelect}
        title={`Open ${mark.label.toLowerCase()} and its note`}
        className="flex min-w-0 flex-1 items-center gap-1.5 text-left cursor-pointer"
      >
        <Icon size={11} strokeWidth={1.75} className="shrink-0 text-muted-foreground/60" />
        <span className={cn('truncate text-label', mark.hidden && 'line-through')}>
          {mark.label}
        </span>
        <span className="ml-auto shrink-0 font-mono text-caption tabular-nums text-muted-foreground/70">
          {mark.value.toFixed(mark.precision)}
        </span>
      </button>
      {/* One cluster, no dead space inside it: the 6px that used to sit between
          the two icons was row — a click there did nothing at all. */}
      <div className="flex shrink-0 items-center">
        <button
          type="button"
          onClick={onToggleHide}
          aria-label={mark.hidden ? `Show ${mark.label}` : `Hide ${mark.label}`}
          className={cn(
            'flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-hover hover:text-foreground cursor-pointer',
            // A hidden level has to advertise its own way back. Focus counts as
            // a reveal too — a keyboard on an opacity-0 button lands nowhere.
            mark.hidden
              ? 'opacity-100'
              : 'opacity-0 group-hover/mark:opacity-100 focus-visible:opacity-100',
          )}
        >
          {mark.hidden ? (
            <EyeOff size={13} strokeWidth={1.75} />
          ) : (
            <Eye size={13} strokeWidth={1.75} />
          )}
        </button>
        <button
          type="button"
          onClick={onDelete}
          aria-label={`Delete ${mark.label}`}
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground opacity-0 hover:bg-hover hover:text-danger group-hover/mark:opacity-100 focus-visible:opacity-100 cursor-pointer"
        >
          <Trash2 size={13} strokeWidth={1.75} />
        </button>
      </div>
    </div>
  )
}

export function WorkbenchTradeIdeaPanel({
  open,
  ideas,
  activeIdeaId,
  expanded,
  onToggleExpand,
  onSelectIdea,
  onSelectMark,
  onToggleHideMark,
  onDeleteMark,
  onDeleteIdea,
  onClose,
}: WorkbenchTradeIdeaPanelProps) {
  if (!open) return null

  return (
    <div
      data-testid="trade-idea-panel"
      // ABOVE the floating strip (z-70), not below it.
      //
      // A docked list and a floating bar shared this region, and the bar won
      // every hit-test — so hovering a level row stopped revealing its controls
      // part-way across, and the eye and the trash could not be pressed at all.
      // The strip now clears this column (`clearLeft`), and this ordering is the
      // guarantee: where a window is too narrow for both, the thing the trader
      // is pointing AT takes the pointer, and the strip — which is draggable —
      // gives way.
      className="absolute bottom-0 left-0 top-0 z-[80] flex flex-col border-r border-border bg-background shadow-popover"
      style={{ width: IDEA_PANEL_W }}
    >
      <div className="flex h-9 shrink-0 items-center gap-1 border-b border-border px-3">
        <span className="flex-1 text-caption uppercase tracking-wide text-muted-foreground">
          Trade ideas
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close trade ideas"
          className="rounded p-0.5 text-muted-foreground hover:text-foreground cursor-pointer"
        >
          <ChevronLeft size={16} strokeWidth={1.75} />
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto py-1">
        {ideas.length === 0 ? (
          <p className="px-3 py-2 text-caption leading-snug text-muted-foreground">
            No trade ideas yet — mark one on the chart with Buy or Sell to see it here.
          </p>
        ) : (
          ideas.map((idea) => {
            const isOpen = expanded.has(idea.ideaId)
            const isActive = idea.ideaId === activeIdeaId
            return (
              <div key={idea.ideaId} className="group/idea">
                <div
                  className={cn(
                    'flex h-8 items-center pr-1 text-label',
                    isActive ? 'bg-muted text-foreground' : 'text-foreground hover:bg-muted',
                  )}
                >
                  <button
                    type="button"
                    onClick={() => onToggleExpand(idea.ideaId)}
                    aria-label={isOpen ? `Collapse ${idea.name}` : `Expand ${idea.name}`}
                    aria-expanded={isOpen}
                    className="flex h-8 w-7 shrink-0 items-center justify-center text-muted-foreground hover:text-foreground cursor-pointer"
                  >
                    {isOpen ? (
                      <ChevronDown size={14} strokeWidth={1.75} />
                    ) : (
                      <ChevronRight size={14} strokeWidth={1.75} />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => onSelectIdea(idea.ideaId)}
                    className="flex min-w-0 flex-1 items-center gap-1.5 text-left cursor-pointer"
                  >
                    {/* A colour hint, not a bullet. */}
                    <span
                      aria-hidden
                      className="h-1.5 w-1.5 shrink-0 rounded-full"
                      style={{ background: idea.color }}
                    />
                    <span className="truncate">{idea.name}</span>
                    <span className="shrink-0 text-caption text-muted-foreground">
                      {idea.dir === 'sell' ? 'Short' : 'Long'}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => onDeleteIdea(idea.ideaId)}
                    aria-label={`Delete ${idea.name}`}
                    // Same 24px box as the level rows below it: the bins line up
                    // in one column, and two sizes in that column read as two
                    // different controls.
                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground opacity-0 hover:bg-hover hover:text-danger group-hover/idea:opacity-100 focus-visible:opacity-100 cursor-pointer"
                  >
                    <Trash2 size={13} strokeWidth={1.75} />
                  </button>
                </div>

                {isOpen &&
                  (idea.marks.length === 0 ? (
                    <p className="px-3 py-1.5 pl-9 text-caption text-muted-foreground">
                      Nothing marked yet.
                    </p>
                  ) : (
                    idea.marks.map((m) => (
                      <MarkRow
                        key={m.id}
                        mark={m}
                        onSelect={() => onSelectMark(m.id)}
                        onToggleHide={() => onToggleHideMark(m.id, !m.hidden)}
                        onDelete={() => onDeleteMark(m.id)}
                      />
                    ))
                  ))}
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}
